import { StockSymbol } from '@benzinga/session';
import { NotesManager, NotesManagerEvent } from '@benzinga/notes-manager';
import React from 'react';
import styled from '@benzinga/themetron';
import { SessionContext } from '@benzinga/session-context';
import Hooks from '@benzinga/hooks';
import { CustomCellRendererProps } from 'ag-grid-react';
import { NotesDialog } from '../../NotesDialog';
import { ColDef } from 'ag-grid-community';
import { noop } from '@benzinga/utils';

interface NoteCellData {
  symbol: StockSymbol;
}

interface OwnProps {
  data: NoteCellData;
  isOnlySymbol?: boolean;
}

type Props = OwnProps & CustomCellRendererProps;

export const NotesTooltipCellRenderer: React.FC<Props> = params => {
  const session = React.useContext(SessionContext);
  const [noteText, setNoteText] = React.useState(() => {
    const notes = session.getManager(NotesManager).getNotesFilteredStored(params.data.symbol);
    return notes[0]?.getText() ?? '';
  });

  Hooks.useSubscriber(session.getManager(NotesManager), (event: NotesManagerEvent) => {
    switch (event.type) {
      case 'notes_updated': {
        const notes = event.notes
          .filter(n => n.getSymbol() === params.data.symbol)
          .reduce((acc, n) =>
            (acc.getUpdatedAtDate() ?? acc.getCreatedAtDate()) > (n.getUpdatedAtDate() ?? n.getCreatedAtDate())
              ? acc
              : n,
          );
        const text = notes ? notes?.getText() : '';
        params?.setValue?.(text);
        setNoteText(text);
        break;
      }
    }
  });

  React.useEffect(() => {
    session.getManager(NotesManager).getNotes();
  }, [session]);

  const onEditClicked = React.useCallback(() => {
    if (params.node.rowIndex !== null) {
      params.api?.startEditingCell({ colKey: 'notes', rowIndex: params.node.rowIndex, ...params });
    }
  }, [params]);

  return (
    <Container>
      <Text>{noteText}</Text>
      <Edit onClick={onEditClicked}>
        <svg
          aria-labelledby="title"
          height="1em"
          preserveAspectRatio="xMidYMid meet"
          viewBox="0 0 24 24"
          width="1em"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title id="title">edit</title>
          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path>
        </svg>
      </Edit>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Text = styled.div`
  flex: 1 1 auto;
  min-width: 0px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Edit = styled.div`
  flex: 0 0 auto;
  cursor: pointer;

  svg {
    vertical-align: middle;
    fill: ${props => props.theme.colors.foreground};
  }
`;

const NotesTooltip = (params: CustomCellRendererProps & { stopEditing: () => void }) => (
  <NotesDialog
    allowEdit={params.api.getEditingCells().length !== 0}
    onClose={params.api.getEditingCells().length !== 0 ? params.stopEditing : noop}
    symbol={params.data.symbol}
  />
);

export const NotesColDef = (colDef: ColDef): ColDef => ({
  cellClass: 'u-alignLeft',
  cellEditor: NotesTooltip,
  cellEditorPopup: true,
  cellRenderer: NotesTooltipCellRenderer,
  editable: true,
  filter: false,
  sortable: false,
  tooltipComponent: NotesTooltip,
  tooltipField: colDef.field,
  ...colDef,
});
